import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Search,
  Clock,
  FileText,
  Server,
  Globe,
  Shield,
  Cpu,
} from 'lucide-react';
import { cn } from '../../utils/cn'
import { AnimatePresence, motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import type { SocketCloudSystemConfigTypes } from '@aaimplatform/socket-cloud-types';
/** Local search result type for console search UI (wire-compatible console nav result). */
type SearchResult = SocketCloudSystemConfigTypes.components['schemas']['ConsoleSearchResult'];

/** Maps the contract's iconName string to the lucide component the overlay renders. */
const iconMap: Record<string, LucideIcon> = {
  FileText,
  Server,
  Globe,
  Shield,
  Cpu,
};

interface RecentSearch {
  query: string;
  timestamp: number;
}

// Mock search results - replace with actual search implementation
const mockSearchResults: SearchResult[] = [
  {
    id: '1',
    title: 'Dashboard',
    description: 'Main system overview and metrics',
    type: 'page',
    path: '/',
    iconName: 'FileText',
  },
  {
    id: '3',
    title: 'Network Topology',
    description: 'Visualize network connections',
    type: 'page',
    path: '/network',
    iconName: 'Globe',
  },
  {
    id: '4',
    title: 'Security Center',
    description: 'Security monitoring and alerts',
    type: 'page',
    path: '/security',
    iconName: 'Shield',
  },
  {
    id: '5',
    title: 'AI/MCP Management',
    description: 'AI agents and MCP protocol',
    type: 'page',
    path: '/ai-mcp',
    iconName: 'Cpu',
  },
];

interface MobileSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileSearchOverlay({ isOpen, onClose }: MobileSearchOverlayProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('aaim:recentSearches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse recent searches:', e);
      }
    }
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle search
  useEffect(() => {
    if (query.trim()) {
      // Mock search implementation - replace with actual search
      const filtered = mockSearchResults.filter(
        item =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          (item.description?.toLowerCase().includes(query.toLowerCase()) ?? false)
      );
      setResults(filtered);
      setSelectedIndex(-1);
    } else {
      setResults([]);
      setSelectedIndex(-1);
    }
  }, [query]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && results[selectedIndex]) {
            handleResultClick(results[selectedIndex]);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const handleResultClick = (result: SearchResult) => {
    // Save to recent searches
    const newRecent: RecentSearch = {
      query: result.title,
      timestamp: Date.now(),
    };
    const updated = [newRecent, ...recentSearches.filter(r => r.query !== result.title)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('aaim:recentSearches', JSON.stringify(updated));

    // Navigate and close
    navigate(result.path);
    setQuery('');
    onClose();
  };

  const handleRecentClick = (recent: RecentSearch) => {
    setQuery(recent.query);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('aaim:recentSearches');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-md lg:hidden"
        >
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-800">
              <Search className="w-6 h-6 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search anything..."
                className={cn(
                  "flex-1 bg-transparent text-lg text-white placeholder-gray-500",
                  "focus:outline-none"
                )}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
              />
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
                aria-label="Close search"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-2">
              {/* Search Results */}
              {query && results.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-1">
                    Results
                  </h3>
                  <div className="space-y-1">
                    {results.map((result, index) => {
                      const ResultIcon = iconMap[result.iconName ?? ''] ?? FileText;
                      return (
                      <button
                        key={result.id}
                        onClick={() => handleResultClick(result)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left",
                          "transition-colors touch-target",
                          "hover:bg-gray-800",
                          selectedIndex === index && "bg-gray-800"
                        )}
                      >
                        <ResultIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white">
                            {result.title}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {result.description}
                          </div>
                        </div>
                      </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No Results */}
              {query && results.length === 0 && (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No results found for "{query}"</p>
                  <p className="text-sm text-gray-500 mt-1">Try searching for something else</p>
                </div>
              )}

              {/* Recent Searches */}
              {!query && recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center justify-between px-2 py-1">
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recent
                    </h3>
                    <button
                      onClick={clearRecentSearches}
                      className="text-xs text-gray-500 hover:text-gray-400"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="space-y-1">
                    {recentSearches.map((recent, index) => (
                      <button
                        key={index}
                        onClick={() => handleRecentClick(recent)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left",
                          "transition-colors touch-target",
                          "hover:bg-gray-800"
                        )}
                      >
                        <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-white flex-1">
                          {recent.query}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Links */}
              {!query && recentSearches.length === 0 && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-1">
                    Quick Links
                  </h3>
                  <div className="space-y-1">
                    {mockSearchResults.slice(0, 5).map((result) => {
                      const ResultIcon = iconMap[result.iconName ?? ''] ?? FileText;
                      return (
                      <button
                        key={result.id}
                        onClick={() => handleResultClick(result)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left",
                          "transition-colors touch-target",
                          "hover:bg-gray-800"
                        )}
                      >
                        <ResultIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white">
                            {result.title}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {result.description}
                          </div>
                        </div>
                      </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer Tips */}
            <div className="p-4 border-t border-gray-800">
              <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-800 rounded">↑↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-800 rounded">Enter</kbd>
                  Select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-800 rounded">Esc</kbd>
                  Close
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}